/**
 * pes-intercom-unified-v3.js — Enhanced PES Supply Intercom integration (v3).
 *
 * Extends v2 with:
 *   10. Shopify cart event tracking (add to cart, cart view, checkout start)
 *   11. Cart abandonment detection (30-min timer → Intercom event)
 *   12. Checkout abandonment detection (1hr timer → Intercom event)
 *   13. Order confirmation / post-purchase flow
 *   14. Search no-results rescue prompt
 *   15. Cart threshold contextual prompt
 *   16. Snitcher → Intercom company push
 *   17. Category-specific contextual prompts (solar, storage, generators)
 *   18. Session intent scoring (repeat visits, category depth, time on site)
 *
 * Voice: wholesale / trade. Staging.pes.supply terminology.
 *   - "169 authorized brands", "trade pricing",
 *   - "169 authorized brands",
 *   - "7-10 business days delivery nationwide",
 * IMPORTANT: Never mention free shipping (PES does not offer it).
 * IMPORTANT: Never mention same-day shipping (PES does not offer it).
 * IMPORTANT: Never mention "112,000+ products" — use "50,000+ SKUs" / "169 authorized brands".
 * IMPORTANT: Never mention "3-5 business days" — use "7-10 business days nationwide".
 *
 * Installation: Load via Shopify ScriptTag or theme.liquid
 * Replaces: pes-intercom-unified-v2.js
 * 2026-10 UPDATE (fin/eol fix): ALL auto-open proactive prompts removed.
 * Messenger now opens ONLY on explicit user click (bubble, badges, BOM,
 * rescue buttons). The $500 cart-threshold prompt force-opened Fin and left
 * stale cart-total text as persistent composer drafts.
 * Cart/abandon/intent EVENT TRACKING is unchanged (trackEvent only, no UI).
 */
(function() {
  /* 2026-09-19 CX fix (quote page double-load): idempotence guard. A stale
     Customer-Events pixel still injects retired theme t/45's
     pes-intercom-loader-v2.js, which boots the messenger a second time and
     replaces window.intercomSettings with a bare {api_base, app_id}.
     This file must never execute twice on one page. */
  if (window.__pesIntercomUnifiedLoaded) { return; }
  window.__pesIntercomUnifiedLoaded = true;
  // ================================================================
  // 0. SNITCHER TRACKING (visitor identification)
  // Replaces RB2B for PES Supply. RB2B kept for Riven suite only.
  // Tracking script ID: sGpnZN5oVX (workspace: portlandiaelectric.supply)
  // ================================================================
  (function(){
    var s = document.createElement('script');
    s.async = true;
    s.type = 'text/javascript';
    s.id = '__radar__';
    s.setAttribute('data-settings', JSON.stringify({
      apiEndpoint: 'radar.snitcher.com',
      cdn: 'cdn.snitcher.com',
      namespace: 'Snitcher',
      profileId: 'sGpnZN5oVX'
    }));
    s.src = 'https://cdn.snitcher.com/releases/latest/radar.min.js';
    var first = document.getElementsByTagName('script')[0];
    if (first && first.parentNode) {
      first.parentNode.insertBefore(s, first);
    } else {
      document.head.appendChild(s);
    }
    window.Snitcher = window.Snitcher || [];
    window.Snitcher._loaded = true;
    ['track','page','identify','group','alias','ready','debug','on','off','once','trackClick','trackSubmit','trackLink','trackForm','pageview','screen','reset','register','setAnonymousId'].forEach(function(method){
      window.Snitcher[method] = function(){
        var args = Array.prototype.slice.call(arguments);
        args.unshift(method);
        window.Snitcher.push(args);
        return window.Snitcher;
      };
    });
    window.Snitcher.bootstrap = function(){};
  })();

  // ================================================================
  // CONFIG
  // ================================================================
  var APP_ID = 'lt1fbeyf';
  var API_BASE = 'https://api.intercom.io';
  var PES_NAVY = '#242833'; /* PES charcoal — PowerLine rebrand 2026-08-28 */
  var PES_GREEN = '#11B67B'; /* PES brand green — PowerLine rebrand 2026-08-28 */
  var PES_ORANGE = '#e07b1c';
  var RB2B_ACCOUNT_ID = 'Z6PVLHQ5PV6R';
  var CART_ABANDON_MINUTES = 30;
  var CHECKOUT_ABANDON_MINUTES = 60;
  var SEARCH_NO_RESULTS_DELAY = 3000; // ms after no-results page load
  var PRODUCT_PROMPT_DELAY = 30000; // 30s on product page before proactive chat

  // Staging trade voice constants
  /* PES PowerLine 2026-08-28: greeting is now page-context aware.
     NOTE: window/document used directly here (w/d are declared below). */
  function buildContextGreeting() {
    var path = window.location.pathname;
    try {
      if (path.indexOf('/products/') !== -1) {
        var ptype = '';
        var ld = document.querySelectorAll('script[type="application/ld+json"]');
        for (var i = 0; i < ld.length; i++) {
          try {
            var data = JSON.parse(ld[i].textContent);
            var prod = Array.isArray(data) ? data[0] : data;
            if (prod && prod['@type'] === 'Product') { ptype = prod.category || ''; break; }
          } catch (e) {}
        }
        if (!ptype) {
          var tEl = document.querySelector('[data-product-type], .product-type');
          if (tEl) ptype = (tEl.getAttribute('data-product-type') || tEl.textContent || '').trim();
        }
        if (!ptype) {
          /* fallback: collection-level crumb from BreadcrumbList JSON-LD
             (e.g. "125kW Generators" -> "125kW generator") */
          for (var j = 0; j < ld.length; j++) {
            try {
              var bc = JSON.parse(ld[j].textContent);
              var bcn = Array.isArray(bc) ? bc[0] : bc;
              if (bcn && bcn['@type'] === 'BreadcrumbList' && bcn.itemListElement && bcn.itemListElement.length > 2) {
                var crumb = bcn.itemListElement[bcn.itemListElement.length - 2];
                var cname = (crumb && (crumb.name || (crumb.item && crumb.item.name))) || '';
                if (cname && cname.toLowerCase() !== 'home') {
                  cname = cname.charAt(0).toLowerCase() + cname.slice(1);
                  if (cname.length > 3 && cname.slice(-1) === 's' && cname.slice(-2) !== 'ss') cname = cname.slice(0, -1);
                  ptype = cname;
                  break;
                }
              }
            } catch (e) {}
          }
        }
        var what = ptype ? ('this ' + String(ptype).toLowerCase()) : 'this product';
        return 'Questions about ' + what + '? Sizing, compatibility, bulk pricing — ask away.';
      }
      if (path.indexOf('/blogs/') !== -1) {
        var og = document.querySelector('meta[property="og:title"]');
        var topic = og ? (og.getAttribute('content') || '') : '';
        if (topic.length > 60) topic = topic.slice(0, 60);
        return topic ? ('Reading "' + topic + '"? We can point you to the right gear or answer spec questions.')
                     : 'Digging into our guides? We can point you to the right gear or answer spec questions.';
      }
      if (path === '/cart' || path.indexOf('/cart/') !== -1) {
        return 'Almost there — need a formal quote, freight estimate, or payment terms before you check out? We can sort it here.';
      }
      if (path.indexOf('/collections/') !== -1) {
        return 'Browsing the catalog? Tell us what you are building and we will pull trade pricing, lead times, and freight.';
      }
      if (path.indexOf('/search') !== -1) {
        return 'Hunting for a part? Send the manufacturer and part number — we source from 300+ manufacturers.';
      }
    } catch (e) {}
    return 'PES Supply — wholesale electrical, solar, and building materials from ' +
      '169 authorized brands. Trade pricing with full OEM warranties. ' +
      'What are you building?';
  }
  var MESSENGER_GREETING = buildContextGreeting();

  // ================================================================
  // 1. INTERCOM BOOT
  // ================================================================
  var w = window;
  var d = document;
  var intercomSettings = {
    api_base: API_BASE,
    app_id: APP_ID,
    /* PES PowerLine 2026-08-28: branded launcher in pes-powerline-chat.js owns
       the visible bubble; native launcher hidden, messenger opens above it. */
    hide_default_launcher: true,
    alignment: 'right',
    horizontal_padding: 20,
    vertical_padding: 96
  };

  /* 2026-09-04 launcher fix: expose settings on window. The Intercom loader
     frame (js.intercomcdn.com frame bundle inside #intercom-frame) reads
     window.parent.intercomSettings for app_id/api_base; trapped in this IIFE
     it never reached the messenger, so no session/launcher ever booted. */
  w.intercomSettings = intercomSettings;

  // 2a. Check for Shopify customer data (injected by theme)
  if (w.Shopify && w.Shopify.customer) {
    intercomSettings.email = w.Shopify.customer.email;
    intercomSettings.name = w.Shopify.customer.name;
    intercomSettings.user_id = String(w.Shopify.customer.id);
    intercomSettings.created_at = Math.floor(new Date(w.Shopify.customer.created_at).getTime() / 1000);
    intercomSettings.shopify_orders_count = w.Shopify.customer.orders_count || 0;
    intercomSettings.shopify_total_spent = w.Shopify.customer.total_spent || 0;
    intercomSettings.customer_type = w.Shopify.customer.tags ? w.Shopify.customer.tags.split(',').map(function(t){return t.trim()})[0] : 'end-user';
    intercomSettings.account_status = 'shopify_customer';
    intercomSettings.lead_source = 'shopify_login';
  }

  // 2b. Check for customer data in meta tags (alternative Shopify method)
  if (!intercomSettings.email) {
    var customerEmailMeta = d.querySelector('meta[name="shopify-customer-email"]');
    var customerNameMeta = d.querySelector('meta[name="shopify-customer-name"]');
    var customerIdMeta = d.querySelector('meta[name="shopify-customer-id"]');
    if (customerEmailMeta && customerEmailMeta.content) {
      intercomSettings.email = customerEmailMeta.content;
      intercomSettings.name = customerNameMeta ? customerNameMeta.content : '';
      intercomSettings.user_id = customerIdMeta ? customerIdMeta.content : '';
      intercomSettings.account_status = 'shopify_customer';
      intercomSettings.lead_source = 'shopify_login';
    }
  }

  // 2c. Identity verification hash — fetch from bridge for logged-in customers
  var BRIDGE_URL = 'https://erp.portlandiaelectric.supply/intercom-bridge';

  function bootIntercom() {
    /* 2026-09-19 CX fix: one messenger, one boot, site-wide. */
    if (w.__pesIntercomBooted) { return; }
    w.__pesIntercomBooted = true;
    var ic = w.Intercom;
    if (typeof ic === 'function') {
      ic('reattach_activator');
      intercomSettings.dynamic_greeting = MESSENGER_GREETING;
      ic('update', intercomSettings);
    } else {
      var i = function() { i.c(arguments); };
      i.q = [];
      i.c = function(args) { i.q.push(args); };
      w.Intercom = i;
      var l = function() {
        if (d.querySelector('script[src*="widget.intercom.io/widget/"]')) { return; } /* 2026-09-19 CX fix: no duplicate widget scripts */
        var s = d.createElement('script');
        s.type = 'text/javascript';
        s.async = true;
        s.src = 'https://widget.intercom.io/widget/' + APP_ID;
        var x = d.getElementsByTagName('script')[0];
        x.parentNode.insertBefore(s, x);
      };
      if (d.readyState === 'complete') { l(); }
      else if (w.attachEvent) { w.attachEvent('onload', l); }
      else { w.addEventListener('load', l, false); }
      intercomSettings.dynamic_greeting = MESSENGER_GREETING;
    }
  }

  if (intercomSettings.email) {
    fetch(BRIDGE_URL + '/identity-hash?email=' + encodeURIComponent(intercomSettings.email))
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (data.user_hash) { intercomSettings.user_hash = data.user_hash; }
        bootIntercom();
      })
      .catch(function() { bootIntercom(); });
  } else {
    bootIntercom();
  }

  // ================================================================
  // 2. RB2B VISITOR IDENTIFICATION CALLBACK
  // ================================================================
  // theme.liquid's RB2B bootstrap pre-sets window.reb2b to a plain object
  // ({loaded: true}) until their CDN script arrives, so a truthiness check is
  // not enough: only reuse the global if it is actually callable, otherwise
  // install our queueing stub. Prevents "w.reb2b is not a function".
  if (typeof w.reb2b !== 'function') {
    var existingQ = (w.reb2b && w.reb2b.q) || [];
    w.reb2b = function() { (w.reb2b.q = w.reb2b.q || []).push(arguments); };
    w.reb2b.q = existingQ;
  }
  w.reb2b.l = +new Date();
  w.reb2b('init', RB2B_ACCOUNT_ID);

  function resolveCompany(companyRaw) {
    if (!companyRaw) return { name: '', industry: '' };
    if (typeof companyRaw === 'string') return { name: companyRaw, industry: '' };
    return { name: companyRaw.name || '', industry: companyRaw.industry || companyRaw.industries || '' };
  }

  function resolveLocation(locationRaw) {
    if (!locationRaw) return { city: '', state: '' };
    if (typeof locationRaw === 'string') return { city: '', state: '' };
    return { city: locationRaw.city || '', state: locationRaw.state || '' };
  }

  function looksLikeSolar(industry) {
    if (!industry) return false;
    var i = String(industry).toLowerCase();
    var keywords = ['solar', 'renewable', 'photovoltaic', 'pv', 'energy storage', 'bess', 'clean energy', 'green energy'];
    for (var k = 0; k < keywords.length; k++) { if (i.indexOf(keywords[k]) !== -1) return true; }
    return false;
  }

  function looksLikeContractor(title) {
    if (!title) return false;
    var t = String(title).toLowerCase();
    return t.indexOf('contractor') !== -1 || t.indexOf('installer') !== -1;
  }

  function looksLikeDeveloper(title) {
    if (!title) return false;
    var t = String(title).toLowerCase();
    return t.indexOf('developer') !== -1 || t.indexOf('epc') !== -1;
  }

  function buildDynamicGreeting(name, title, companyName, industry) {
    var firstName = (name || '').split(' ')[0] || name || 'there';
    if (looksLikeContractor(title)) {
      return 'Welcome back, ' + firstName + '. Sourcing for a job? We carry ' +
        '169 authorized brands with trade pricing. What are you building?';
    }
    if (looksLikeDeveloper(title)) {
      return 'Welcome, ' + firstName + '. Need a full project BOM? We source from ' +
        '169 authorized brands and can price the whole ' +
        'package including freight.';
    }
    if (looksLikeSolar(industry)) {
      return 'Welcome, ' + firstName + '. We carry Tier 1 panels, commercial ' +
        'inverters, and BESS from 169 authorized brands. Tell us about your project.';
    }
    if (companyName) {
      return 'Welcome, ' + firstName + ' from ' + companyName +
        '. What can we help you source today?';
    }
    return 'Welcome, ' + firstName + '. What can we help you source today?';
  }

  // Register identify callback — fires when RB2B resolves a visitor
  w.reb2b('identify', function(visitor) {
    if (!visitor || (!visitor.email && !visitor.workEmail)) return;

    var email = visitor.email || visitor.workEmail;
    var name = visitor.name || [visitor.first_name, visitor.last_name].filter(Boolean).join(' ');
    var companyResolved = resolveCompany(visitor.company);
    var companyName = companyResolved.name;
    var industry = companyResolved.industry;
    var title = visitor.title || visitor.job_title || '';
    var locResolved = resolveLocation(visitor.location);
    var city = locResolved.city;
    var state = locResolved.state;
    var dynamicGreeting = buildDynamicGreeting(name, title, companyName, industry);

    var currentEmail = intercomSettings.email;
    if (currentEmail && currentEmail.toLowerCase() === email.toLowerCase()) {
      w.Intercom('update', {
        company_name: companyName,
        lead_source: 'rb2b',
        rb2b_title: title,
        rb2b_company: companyName,
        rb2b_industry: industry,
        rb2b_location: [city, state].filter(Boolean).join(', '),
        rb2b_identified_at: Math.floor(Date.now() / 1000),
        dynamic_greeting: dynamicGreeting
      });
    } else {
      w.Intercom('update', {
        email: email,
        name: name || undefined,
        company_name: companyName || undefined,
        lead_source: 'rb2b',
        account_status: 'rb2b_identified',
        rb2b_title: title,
        rb2b_company: companyName,
        rb2b_industry: industry,
        rb2b_linkedin: visitor.linkedin_url || visitor.linkedin || undefined,
        rb2b_location: [city, state].filter(Boolean).join(', ') || undefined,
        rb2b_identified_at: Math.floor(Date.now() / 1000),
        dynamic_greeting: dynamicGreeting
      });
    }

    // NEW: Push company to Snitcher for tracking
    if (companyName && window.Snitcher) {
      window.Snitcher.group({
        name: companyName,
        industry: industry || undefined,
        website: visitor.domain || undefined
      });
    }
  });

  // Load RB2B script — DISABLED 2026-10-17: cdn.rb2b.com DNS dead
  // (ERR_NAME_NOT_RESOLVED on every page; Clarity script-error source).
  // Per file doctrine RB2B is Riven-suite-only; Snitcher carries PES visitor ID.
  /*   (function() {
    var s = d.createElement('script');
    s.type = 'text/javascript';
    s.async = true;
    s.src = 'https://cdn.rb2b.com/script.js';
    var x = d.getElementsByTagName('script')[0];
    x.parentNode.insertBefore(s, x);
  })();
  */

  // ================================================================
  // 3. PRODUCT PAGE CHAT (from v2, enhanced)
  // ================================================================
  function waitForIntercom(cb, attempts) {
    attempts = attempts || 0;
    if (typeof w.Intercom === 'function') { cb(); }
    else if (attempts < 50) { setTimeout(function() { waitForIntercom(cb, attempts + 1); }, 200); }
  }

  function getProductInfo() {
    var info = { title: '', url: w.location.href, sku: '', price: '', vendor: '', type: '' };
    var ldScripts = d.querySelectorAll('script[type="application/ld+json"]');
    for (var i = 0; i < ldScripts.length; i++) {
      try {
        var data = JSON.parse(ldScripts[i].textContent);
        if (data['@type'] === 'Product' || (Array.isArray(data) && data[0] && data[0]['@type'] === 'Product')) {
          var product = Array.isArray(data) ? data[0] : data;
          info.title = product.name || '';
          info.sku = product.sku || '';
          if (product.offers) {
            info.price = product.offers.price || '';
            if (product.offers.priceCurrency) { info.price = info.price + ' ' + product.offers.priceCurrency; }
          }
          break;
        }
      } catch (e) {}
    }
    if (!info.title) {
      var ogTitle = d.querySelector('meta[property="og:title"]');
      if (ogTitle) info.title = ogTitle.getAttribute('content') || '';
    }
    if (!info.title) {
      var h1 = d.querySelector('h1.product-title, h1.product__title, .product-single__title');
      if (h1) info.title = h1.textContent.trim();
    }
    if (!info.sku) {
      var skuEl = d.querySelector('[data-sku], .product-sku, .variant-sku');
      if (skuEl) info.sku = skuEl.getAttribute('data-sku') || skuEl.textContent.trim();
    }
    // NEW: Extract vendor and product type
    var vendorEl = d.querySelector('[data-vendor], .product-vendor, .vendor');
    if (vendorEl) info.vendor = vendorEl.getAttribute('data-vendor') || vendorEl.textContent.trim();
    var typeEl = d.querySelector('[data-product-type], .product-type');
    if (typeEl) info.type = typeEl.getAttribute('data-product-type') || typeEl.textContent.trim();
    return info;
  }

  function createProductButton(product) {
    if (d.getElementById('pes-product-chat-btn')) return;
    var btn = d.createElement('div');
    btn.id = 'pes-product-chat-btn';
    btn.style.cssText = [
      'position: fixed', 'bottom: ' + (isCartPage() ? '132px' : '88px'), 'right: 20px',
      'z-index: 2147483646', 'background: ' + PES_NAVY, 'color: #fff',
      'padding: 10px 20px', 'border-radius: 24px',
      'font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      'font-size: 14px', 'font-weight: 600', 'cursor: pointer',
      'box-shadow: 0 4px 12px rgba(0,0,0,0.15)',
      'transition: transform 0.2s, box-shadow 0.2s',
      'display: flex', 'align-items: center', 'gap: 6px',
      'user-select: none', '-webkit-user-select: none'
    ].join(';');
    btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg> Ask about this product';
    btn.addEventListener('mouseenter', function() {
      btn.style.transform = 'translateY(-2px)';
      btn.style.boxShadow = '0 6px 16px rgba(0,0,0,0.2)';
    });
    btn.addEventListener('mouseleave', function() {
      btn.style.transform = 'translateY(0)';
      btn.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
    });
    btn.addEventListener('click', function() {
      var message = 'I have a question about this product:\n\n';
      message += 'Product: ' + (product.title || 'Unknown') + '\n';
      message += 'URL: ' + product.url + '\n';
      if (product.sku) message += 'SKU: ' + product.sku + '\n';
      if (product.price) message += 'Price: ' + product.price + '\n';
      if (product.vendor) message += 'Brand: ' + product.vendor + '\n';
      if (typeof w.Intercom === 'function') {
        w.Intercom('show');
        setTimeout(function() { w.Intercom('showNewMessage', message); }, 300);
      } else { w.alert('Chat is loading, please try again in a moment.'); }
    });
    d.body.appendChild(btn);
  }

  // ================================================================
  // 3b. PRICE-MATCH BADGE (from v2)
  // ================================================================
  function findPriceElement() {
    var selectors = ['.price__regular .price-item', '.price-item--regular', '.product__price',
      '.product-single__price', '[data-price]', '.price', '.product-price'];
    for (var i = 0; i < selectors.length; i++) {
      var el = d.querySelector(selectors[i]);
      if (el) return el;
    }
    return null;
  }

  function createPriceMatchBadge(product) {
    if (d.getElementById('pes-price-match-badge')) return;
    var priceEl = findPriceElement();
    var badge = d.createElement('div');
    badge.id = 'pes-price-match-badge';
    badge.style.cssText = [
      'display: inline-flex', 'align-items: center', 'gap: 4px',
      'margin-top: 6px', 'margin-bottom: 6px', 'padding: 4px 10px',
      'font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      'font-size: 12px', 'font-weight: 600', 'color: ' + PES_GREEN,
      'background: rgba(45,134,89,0.10)', 'border: 1px solid rgba(45,134,89,0.35)',
      'border-radius: 14px', 'cursor: pointer',
      'user-select: none', '-webkit-user-select: none',
      'transition: background 0.2s, box-shadow 0.2s'
    ].join(';');
    badge.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path><path d="M4 6v12a2 2 0 0 0 2 2h14v-4"></path><path d="M18 12a2 2 0 0 0 0 4h4v-4Z"></path></svg> Found a lower price? We&rsquo;ll match it.';
    badge.addEventListener('mouseenter', function() { badge.style.background = 'rgba(45,134,89,0.18)'; badge.style.boxShadow = '0 2px 8px rgba(0,0,0,0.10)'; });
    badge.addEventListener('mouseleave', function() { badge.style.background = 'rgba(45,134,89,0.10)'; badge.style.boxShadow = 'none'; });
    badge.addEventListener('click', function() {
      var message = 'I found a lower price on this product:\n\n';
      message += 'Product: ' + (product.title || 'Unknown') + '\n';
      if (product.sku) message += 'SKU: ' + product.sku + '\n';
      message += 'URL: ' + product.url + '\n\n';
      message += 'Competitor price: \nCompetitor URL: ';
      if (typeof w.Intercom === 'function') {
        w.Intercom('show');
        setTimeout(function() { w.Intercom('showNewMessage', message); }, 300);
      } else { w.alert('Chat is loading, please try again in a moment.'); }
    });
    if (priceEl && priceEl.parentNode) { priceEl.parentNode.insertBefore(badge, priceEl.nextSibling); }
    else {
      var form = d.querySelector('form[action*="/cart/add"], .product-form, .product__form');
      if (form) { form.parentNode.insertBefore(badge, form.nextSibling); }
      else {
        badge.style.position = 'fixed'; badge.style.bottom = '132px'; badge.style.right = '20px'; badge.style.zIndex = '2147483646';
        d.body.appendChild(badge);
      }
    }
  }

  // ================================================================
  // 3c. BOM INTAKE BUTTON (from v2)
  // ================================================================
  function isCartPage() { var p = w.location.pathname; return p === '/cart' || p.indexOf('/cart/') !== -1; }
  function isCollectionPage() { var p = w.location.pathname; return p.indexOf('/collections/') !== -1 && p.indexOf('/products/') === -1; }
  function isProductPage() { return w.location.pathname.indexOf('/products/') !== -1; }
  function isOrderStatusPage() { var p = w.location.pathname; return p.indexOf('/orders/') !== -1 || p.indexOf('/account/orders') !== -1; }
  function isCheckoutPage() { var p = w.location.pathname; return p.indexOf('/checkouts/') !== -1 || p.indexOf('/checkout') !== -1; }
  function isSearchPage() { return w.location.pathname.indexOf('/search') !== -1; }

  function createBomButton() {
    if (d.getElementById('pes-bom-btn')) return;
    if (!isCartPage() && !isCollectionPage()) return;
    var btn = d.createElement('div');
    btn.id = 'pes-bom-btn';
    btn.style.cssText = [
      'position: fixed', 'bottom: 88px', 'right: 20px', 'z-index: 2147483646',
      'background: ' + PES_GREEN, 'color: #fff', 'padding: 10px 20px', 'border-radius: 24px',
      'font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      'font-size: 14px', 'font-weight: 600', 'cursor: pointer',
      'box-shadow: 0 4px 12px rgba(0,0,0,0.15)',
      'transition: transform 0.2s, box-shadow 0.2s',
      'display: flex', 'align-items: center', 'gap: 6px',
      'user-select: none', '-webkit-user-select: none'
    ].join(';');
    btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 11l3 3L22 4"></path><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></svg> Submit BOM';
    btn.addEventListener('mouseenter', function() { btn.style.transform = 'translateY(-2px)'; btn.style.boxShadow = '0 6px 16px rgba(0,0,0,0.2)'; });
    btn.addEventListener('mouseleave', function() { btn.style.transform = 'translateY(0)'; btn.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'; });
    btn.addEventListener('click', function() {
      var message = 'I\'d like to submit a Bill of Materials for pricing. Here\'s what I need:\n\n' +
        '[Paste your BOM here — product names, SKUs, quantities]\n\nDelivery zip code: \nTimeline: ';
      if (typeof w.Intercom === 'function') {
        w.Intercom('show');
        setTimeout(function() { w.Intercom('showNewMessage', message); }, 300);
      } else { w.alert('Chat is loading, please try again in a moment.'); }
    });
    d.body.appendChild(btn);
  }

  // ================================================================
  // NEW SECTION 10: SHOPIFY CART EVENT TRACKING
  // ================================================================

  var cartState = {
    token: null,
    item_count: 0,
    total_price: 0,
    items: [],
    last_updated: null,
    checkout_started: false,
    abandon_timer: null,
    checkout_abandon_timer: null
  };

  /**
   * Fetch the current Shopify cart via AJAX.
   */
  function fetchCart() {
    return fetch('/cart.js', {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    }).then(function(r) { return r.json(); });
  }

  /**
   * Track cart events in Intercom.
   * Uses Intercom('trackEvent') for each cart milestone.
   */
  function trackCartEvent(eventName, metadata) {
    if (typeof w.Intercom === 'function') {
      w.Intercom('trackEvent', eventName, metadata || {});
    }
    // Also track in Snitcher
    if (window.Snitcher) {
      window.Snitcher.track(eventName, metadata || {});
    }
    console.log('[PES] Tracked:', eventName, metadata);
  }

  /**
   * Update cart state and trigger abandonment timers.
   */
  function updateCartState(cart) {
    var wasEmpty = cartState.item_count === 0;
    var nowHasItems = cart.item_count > 0;
    var wasCheckout = cartState.checkout_started;

    cartState.token = cart.token;
    cartState.item_count = cart.item_count;
    cartState.total_price = cart.total_price;
    cartState.items = cart.items || [];
    cartState.last_updated = Math.floor(Date.now() / 1000);

    // Cart created (was empty, now has items)
    if (wasEmpty && nowHasItems) {
      trackCartEvent('cart_created', {
        cart_token: cart.token,
        item_count: cart.item_count,
        total_price: (cart.total_price / 100).toFixed(2),
        first_item: cart.items[0] ? cart.items[0].title : '',
        first_item_vendor: cart.items[0] ? cart.items[0].vendor : '',
        page_url: w.location.href
      });

      // Start abandonment timer
      startCartAbandonTimer(cart);
    }

    // Cart updated (items changed)
    if (!wasEmpty && nowHasItems) {
      trackCartEvent('cart_updated', {
        cart_token: cart.token,
        item_count: cart.item_count,
        total_price: (cart.total_price / 100).toFixed(2),
        page_url: w.location.href
      });
      // Reset abandonment timer on update
      startCartAbandonTimer(cart);
    }

    // Cart emptied
    if (!wasEmpty && !nowHasItems) {
      trackCartEvent('cart_emptied', {});
      clearCartAbandonTimer();
    }


    // Cart threshold prompt REMOVED 2026-10 (fin/eol fix): it force-opened the
    // messenger with a cart-total prefill that went stale. Tracking above unchanged.
  }

  /**
   * Start or reset the cart abandonment timer.
   * If the user doesn't reach checkout within X minutes, fire abandonment event.
   */
  function startCartAbandonTimer(cart) {
    clearCartAbandonTimer();
    cartState.abandon_timer = setTimeout(function() {
      if (!cartState.checkout_started && cartState.item_count > 0) {
        trackCartEvent('cart_abandoned', {
          cart_token: cartState.token,
          item_count: cartState.item_count,
          total_price: (cartState.total_price / 100).toFixed(2),
          items: cartState.items.map(function(i) { return i.title + ' x' + i.quantity; }).join(', '),
          abandon_duration_min: CART_ABANDON_MINUTES,
          page_url: w.location.href
        });

      }
    }, CART_ABANDON_MINUTES * 60 * 1000);
  }

  function clearCartAbandonTimer() {
    if (cartState.abandon_timer) {
      clearTimeout(cartState.abandon_timer);
      cartState.abandon_timer = null;
    }
  }

  /**
   * Start checkout abandonment timer.
   */
  function startCheckoutAbandonTimer() {
    if (cartState.checkout_abandon_timer) clearTimeout(cartState.checkout_abandon_timer);
    cartState.checkout_started = true;
    clearCartAbandonTimer();

    cartState.checkout_abandon_timer = setTimeout(function() {
      if (cartState.checkout_started && !isOrderStatusPage()) {
        trackCartEvent('checkout_abandoned', {
          cart_token: cartState.token,
          item_count: cartState.item_count,
          total_price: (cartState.total_price / 100).toFixed(2),
          abandon_duration_min: CHECKOUT_ABANDON_MINUTES,
          page_url: w.location.href
        });

      }
    }, CHECKOUT_ABANDON_MINUTES * 60 * 1000);
  }

  // SECTION 11 (cart threshold prompt) REMOVED 2026-10 (fin/eol fix) — see header.

  // SECTION 12 (proactive-message helper) REMOVED 2026-10 (fin/eol fix) — no
  // code path may force-open the messenger; user clicks open it directly.

  // ================================================================
  // NEW SECTION 13: CHECKOUT TRACKING
  // ================================================================

  function initCheckoutTracking() {
    if (!isCheckoutPage()) return;

    // Track checkout started
    trackCartEvent('checkout_started', {
      cart_token: cartState.token,
      item_count: cartState.item_count,
      total_price: (cartState.total_price / 100).toFixed(2),
      page_url: w.location.href
    });

    // Start checkout abandonment timer
    startCheckoutAbandonTimer();

    // Monitor for checkout completion (Shopify redirects to /orders/ or /thank_you)
    var checkoutInterval = setInterval(function() {
      if (isOrderStatusPage()) {
        clearInterval(checkoutInterval);
        trackCartEvent('checkout_completed', {
          cart_token: cartState.token,
          page_url: w.location.href
        });
        clearCartAbandonTimer();
        if (cartState.checkout_abandon_timer) {
          clearTimeout(cartState.checkout_abandon_timer);
          cartState.checkout_abandon_timer = null;
        }
        cartState.checkout_started = false;
      }
    }, 2000);
  }

  // ================================================================
  // NEW SECTION 14: ORDER CONFIRMATION / POST-PURCHASE
  // ================================================================

  function initOrderConfirmation() {
    if (!isOrderStatusPage()) return;

    // Track order placed
    trackCartEvent('order_placed', {
      page_url: w.location.href
    });


    // Clear all abandonment timers
    clearCartAbandonTimer();
    if (cartState.checkout_abandon_timer) {
      clearTimeout(cartState.checkout_abandon_timer);
      cartState.checkout_abandon_timer = null;
    }
    cartState.checkout_started = false;
  }

  // ================================================================
  // NEW SECTION 15: SEARCH NO-RESULTS RESCUE
  // ================================================================

  function initSearchNoResults() {
    if (!isSearchPage()) return;

    // Check if search returned no results
    var noResultsEl = d.querySelector('.search-no-results, .template-search__empty, .no-results, #SearchEmpty');
    var resultsCount = d.querySelector('.search-results-count, .template-search__results-count');
    var hasNoResults = false;

    if (noResultsEl) {
      hasNoResults = true;
    } else if (resultsCount) {
      var text = resultsCount.textContent.toLowerCase();
      if (text.indexOf('0 results') !== -1 || text.indexOf('no results') !== -1 || text.indexOf('found 0') !== -1) {
        hasNoResults = true;
      }
    }

    // Also check if there are no product cards
    if (!hasNoResults) {
      var productCards = d.querySelectorAll('.product-card, .card--product, .search-result-item, .grid__item');
      if (productCards.length === 0) hasNoResults = true;
    }

    if (hasNoResults) {
      // Extract the search query
      var searchInput = d.querySelector('input[type="search"], input[name="q"]');
      var searchQuery = searchInput ? searchInput.value : '';

      // Show rescue prompt after delay
      setTimeout(function() {
        var message = 'Could not find what you were looking for';
        if (searchQuery) message += ' for "' + searchQuery + '"';
        message += '? We carry 50,000+ SKUs from 169 authorized brands and ' +
          'can source from 300+ manufacturers. Send us the part number, ' +
          'manufacturer, or product description and we will find it for you.';

        // Create a visual prompt above the no-results area
        var rescueBanner = d.createElement('div');
        rescueBanner.id = 'pes-search-rescue';
        rescueBanner.style.cssText = [
          'max-width: 600px', 'margin: 24px auto', 'padding: 20px 28px',
          'background: ' + PES_NAVY, 'color: #fff', 'border-radius: 12px',
          'text-align: center', 'font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          'box-shadow: 0 4px 16px rgba(0,0,0,0.12)'
        ].join(';');
        rescueBanner.innerHTML =
          '<p style="margin:0 0 12px;font-size:16px;font-weight:600;">' +
          'Can\'t find what you need?</p>' +
          '<p style="margin:0 0 16px;font-size:14px;opacity:0.9;">' +
          'We source from 300+ manufacturers and 169 authorized brands. ' +
          'Send us a part number or description and we\'ll find it.</p>' +
          '<button id="pes-search-rescue-btn" style="' +
          'background:' + PES_GREEN + ';color:#fff;border:none;padding:10px 24px;' +
          'border-radius:24px;font-size:14px;font-weight:600;cursor:pointer;' +
          'font-family:inherit;transition:transform 0.2s;">' +
          'Send us your request</button>';

        var mainContent = d.querySelector('main, .main-content, #MainContent, .template-search');
        if (mainContent) {
          mainContent.insertBefore(rescueBanner, mainContent.firstChild);
        } else {
          rescueBanner.style.margin = '24px 20px';
          d.body.insertBefore(rescueBanner, d.body.firstChild);
        }

        var rescueBtn = d.getElementById('pes-search-rescue-btn');
        if (rescueBtn) {
          rescueBtn.addEventListener('mouseenter', function() { rescueBtn.style.transform = 'scale(1.05)'; });
          rescueBtn.addEventListener('mouseleave', function() { rescueBtn.style.transform = 'scale(1)'; });
          rescueBtn.addEventListener('click', function() {
            var msg = 'I am looking for a product I could not find on your site:\n\n';
            if (searchQuery) msg += 'Search term: "' + searchQuery + '"\n';
            msg += 'Manufacturer / brand: \n';
            msg += 'Part number / SKU: \n';
            msg += 'Quantity needed: \n';
            msg += 'Delivery zip code: \n';
            msg += 'Timeline: ';
            if (typeof w.Intercom === 'function') {
              w.Intercom('show');
              setTimeout(function() { w.Intercom('showNewMessage', msg); }, 300);
            }
          });
        }

        // Also fire Intercom event
        trackCartEvent('search_no_results', {
          search_query: searchQuery,
          page_url: w.location.href
        });
      }, SEARCH_NO_RESULTS_DELAY);
    }
  }

  // ================================================================
  // NEW SECTION 16: CATEGORY-SPECIFIC CONTEXTUAL PROMPTS
  // ================================================================

  var categoryPrompts = {
    'solar-panels': {
      message: 'Sourcing solar panels? We carry Tier 1 monocrystalline and bifacial ' +
        'modules from 169 authorized brands. Tell us your wattage, form factor, and ' +
        'quantity for a trade quote.',
      promptId: 'category_solar_panels'
    },
    'inverters': {
      message: 'Need an inverter? We carry string inverters, microinverters, and ' +
        'hybrid systems with full OEM warranties. What is your array size and ' +
        'system configuration?',
      promptId: 'category_inverters'
    },
    'batteries': {
      message: 'Looking at energy storage? We carry BESS, lithium, and lead-acid ' +
        'solutions from authorized brands. What capacity and voltage do you need?',
      promptId: 'category_storage'
    },
    'generators': {
      message: 'Need a generator? We carry standby, portable, and commercial units. ' +
        'Tell us your power requirements (kW), fuel type, and application for a ' +
        'quote with freight.',
      promptId: 'category_generators'
    },
    'electrical': {
      message: 'Looking for electrical equipment? We carry panels, breakers, conduit, ' +
        'and components from 169 authorized brands. What are you wiring?',
      promptId: 'category_electrical'
    }
  };

  function detectCategory() {
    var path = w.location.pathname.toLowerCase();
    // Check collection path
    for (var key in categoryPrompts) {
      if (path.indexOf('/collections/' + key) !== -1 || path.indexOf(key) !== -1) {
        return key;
      }
    }
    // Check product type from JSON-LD
    var ldScripts = d.querySelectorAll('script[type="application/ld+json"]');
    for (var i = 0; i < ldScripts.length; i++) {
      try {
        var data = JSON.parse(ldScripts[i].textContent);
        if (data['@type'] === 'Product' || (Array.isArray(data) && data[0] && data[0]['@type'] === 'Product')) {
          var product = Array.isArray(data) ? data[0] : data;
          var category = (product.category || '').toLowerCase();
          for (var k in categoryPrompts) {
            if (category.indexOf(k.replace('-', ' ')) !== -1 || category.indexOf(k) !== -1) {
              return k;
            }
          }
        }
      } catch (e) {}
    }
    return null;
  }


  // ================================================================
  // NEW SECTION 17: SESSION INTENT SCORING
  // ================================================================

  var sessionIntent = {
    pageViews: 0,
    productViews: 0,
    categoryViews: 0,
    searchCount: 0,
    cartActions: 0,
    timeOnSite: 0,
    repeatVisitor: false,
    visitedCategories: {},
    score: 0
  };

  function initIntentScoring() {
    // Track page views
    sessionIntent.pageViews++;

    // Detect repeat visitor
    var lastVisit = localStorage.getItem('pes_last_visit');
    if (lastVisit) {
      var daysSince = (Date.now() - parseInt(lastVisit)) / (1000 * 60 * 60 * 24);
      if (daysSince < 30) sessionIntent.repeatVisitor = true;
    }
    localStorage.setItem('pes_last_visit', Date.now().toString());

    // Track product views
    if (isProductPage()) sessionIntent.productViews++;
    if (isCollectionPage()) {
      sessionIntent.categoryViews++;
      var cat = detectCategory();
      if (cat) sessionIntent.visitedCategories[cat] = (sessionIntent.visitedCategories[cat] || 0) + 1;
    }

    // Track time on site
    var startTime = Date.now();
    setInterval(function() {
      sessionIntent.timeOnSite = Math.floor((Date.now() - startTime) / 1000);
      calculateIntentScore();
    }, 30000); // Update every 30 seconds

    // Update Intercom with intent data periodically
    setInterval(function() {
      if (typeof w.Intercom === 'function' && sessionIntent.score > 0) {
        w.Intercom('update', {
          session_page_views: sessionIntent.pageViews,
          session_product_views: sessionIntent.productViews,
          session_time_on_site: sessionIntent.timeOnSite,
          session_intent_score: sessionIntent.score,
          repeat_visitor: sessionIntent.repeatVisitor
        });
      }
    }, 60000); // Push to Intercom every 60 seconds
  }

  function calculateIntentScore() {
    var score = 0;
    // Page views: +1 each, max 10
    score += Math.min(sessionIntent.pageViews, 10);
    // Product views: +2 each, max 20
    score += Math.min(sessionIntent.productViews * 2, 20);
    // Category views: +3 each, max 15
    score += Math.min(sessionIntent.categoryViews * 3, 15);
    // Search: +5 each, max 15
    score += Math.min(sessionIntent.searchCount * 5, 15);
    // Cart actions: +10 each, max 30
    score += Math.min(sessionIntent.cartActions * 10, 30);
    // Time on site: +1 per minute, max 10
    score += Math.min(Math.floor(sessionIntent.timeOnSite / 60), 10);
    // Repeat visitor: +10
    if (sessionIntent.repeatVisitor) score += 10;
    sessionIntent.score = score;
  }

  // ================================================================
  // NEW SECTION 18: CART AJAX INTERCEPT
  // ================================================================

  /**
   * Intercept Shopify cart AJAX requests to track cart events.
   * Shopify uses /cart/add.js, /cart/change.js, /cart/update.js
   */
  function interceptCartAjax() {
    var originalFetch = w.fetch;
    w.fetch = function() {
      var url = arguments[0];
      var options = arguments[1] || {};
      var promise = originalFetch.apply(this, arguments);

      if (typeof url === 'string' && url.indexOf('/cart') !== -1) {
        promise.then(function(response) {
          if (response.ok && url.indexOf('.js') !== -1) {
            response.clone().json().then(function(cart) {
              if (cart && cart.token) {
                updateCartState(cart);
                sessionIntent.cartActions++;
              }
            }).catch(function() {});
          }
        }).catch(function() {});
      }
      return promise;
    };

    // Also intercept XMLHttpRequest for older Shopify themes
    var originalXHROpen = XMLHttpRequest.prototype.open;
    var originalXHRSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function(method, url) {
      this._pes_url = url;
      return originalXHROpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function() {
      var self = this;
      var originalOnLoad = this.onload;
      this.onload = function() {
        if (self._pes_url && self._pes_url.indexOf('/cart') !== -1 && self._pes_url.indexOf('.js') !== -1) {
          try {
            var cart = JSON.parse(self.responseText);
            if (cart && cart.token) {
              updateCartState(cart);
              sessionIntent.cartActions++;
            }
          } catch (e) {}
        }
        if (originalOnLoad) originalOnLoad.apply(self, arguments);
      };
      return originalXHRSend.apply(this, arguments);
    };
  }

  // ================================================================
  // PAGE-TYPE INIT (enhanced from v2)
  // ================================================================

  function initProductChat() {
    if (!isProductPage()) return;
    waitForIntercom(function() {
      var product = getProductInfo();
      if (product.title) {
        /* PES facelift 2026-11 (item 1): duplicate "Ask about this product" pill removed.
           The Intercom messenger owns the single chat bubble; native product context is
           still pushed to the messenger via Intercom('update') below. */
        /* createProductButton(product); */
        createPriceMatchBadge(product);
      }
      if (typeof w.Intercom === 'function') {
        w.Intercom('update', {
          current_product: product.title,
          current_product_url: product.url,
          current_product_sku: product.sku || undefined,
          current_product_vendor: product.vendor || undefined
        });
      }
      // Track product view event
      trackCartEvent('product_viewed', {
        product_title: product.title,
        product_sku: product.sku,
        product_vendor: product.vendor,
        product_url: product.url,
        page_url: w.location.href
      });
    });
  }

  function initBomButton() {
    if (!isCartPage() && !isCollectionPage()) return;
    waitForIntercom(function() { createBomButton(); });
  }

  function initPageFeatures() {
    initProductChat();
    initBomButton();
    initCheckoutTracking();
    initOrderConfirmation();
    initSearchNoResults();
    /* category auto-open prompt REMOVED 2026-10 (fin/eol fix): was 45s collection auto-open. */
  }

  // ================================================================
  // INITIALIZE EVERYTHING
  // ================================================================

  // Intercept cart AJAX first
  interceptCartAjax();

  // Initialize page features
  if (d.readyState === 'loading') {
    d.addEventListener('DOMContentLoaded', initPageFeatures);
  } else {
    initPageFeatures();
  }

  // Initialize intent scoring
  if (d.readyState === 'loading') {
    d.addEventListener('DOMContentLoaded', initIntentScoring);
  } else {
    initIntentScoring();
  }

  // Track search events
  if (isSearchPage()) {
    sessionIntent.searchCount++;
    var searchInput = d.querySelector('input[type="search"], input[name="q"]');
    if (searchInput && searchInput.value) {
      trackCartEvent('search_performed', {
        search_query: searchInput.value,
        page_url: w.location.href
      });
    }
  }

  // Initial cart check on page load
  if (!isCheckoutPage() && !isOrderStatusPage()) {
    fetchCart().then(function(cart) {
      if (cart && cart.item_count > 0) {
        cartState.token = cart.token;
        cartState.item_count = cart.item_count;
        cartState.total_price = cart.total_price;
        cartState.items = cart.items || [];
        cartState.last_updated = Math.floor(Date.now() / 1000);
        // User has items in cart — start abandonment timer
        startCartAbandonTimer(cart);
      }
    }).catch(function() {});
  }

  // SPA route change detection
  var lastUrl = w.location.href;
  setInterval(function() {
    /* PES facelift 2026-11 (item 1): a stale Customer-Events pixel still injects
       theme t/45's pes-intercom-loader-v2.js, which recreates the retired
       "Ask about this product" pill. The messenger owns the single bubble, so
       remove the pill whenever it appears (500 ms guard, id must stay in sync
       with the retired loader). */
    /* 2026-09-19 CX fix: the pixel's loader-v2 REPLACES window.intercomSettings
       with {api_base, app_id}, wiping identity, the dynamic greeting, and
       hide_default_launcher. Re-assert the canonical object if swapped. */
    if (w.intercomSettings !== intercomSettings) {
      w.intercomSettings = intercomSettings;
      if (typeof w.Intercom === 'function') {
        try { w.Intercom('update', intercomSettings); } catch (e) {}
      }
    }

    var dupePill = d.getElementById('pes-product-chat-btn');
    if (dupePill) dupePill.remove();
    if (w.location.href !== lastUrl) {
      lastUrl = w.location.href;
      // Clean up old elements
      var oldBtn = d.getElementById('pes-product-chat-btn');
      if (oldBtn) oldBtn.remove();
      var oldBadge = d.getElementById('pes-price-match-badge');
      if (oldBadge) oldBadge.remove();
      var oldBom = d.getElementById('pes-bom-btn');
      if (oldBom) oldBom.remove();
      var oldRescue = d.getElementById('pes-search-rescue');
      if (oldRescue) oldRescue.remove();

      // Re-init for new page
      setTimeout(initPageFeatures, 1000);
      sessionIntent.pageViews++;
      if (isProductPage()) sessionIntent.productViews++;
      if (isCollectionPage()) sessionIntent.categoryViews++;
    }
  }, 500);
})();
